import type { MultiStepperStepMeta } from 'components/organisms/MultiStepper';

/** Full-screen first-login wizard (authenticated). */
export const ADMIN_INITIAL_SETUP_PATH = '/onboarding-setup';

/** Non–super users while tenant onboarding is incomplete (no access to wizard). */
export const ADMIN_ONBOARDING_PENDING_PATH = '/onboarding-pending';

/** Default landing after the wizard completes — same as the post-login default. */
export const ADMIN_INITIAL_POST_SETUP_PATH = '/';

/**
 * Pass as `navigate(..., { state })` when opening {@link ADMIN_INITIAL_SETUP_PATH} from in-app UI.
 * Skipped tenants are otherwise redirected to the main app by `OnboardingGuard` (same as completed).
 */
export const ONBOARDING_WIZARD_RESUME_LOCATION_STATE = {
  resumeOnboardingWizard: true as const,
};

/**
 * Set when `OnboardingGuard` redirects a skipped tenant from the wizard to `/`.
 * Main shell shows a loader until tenant context includes `is_onboarding_skipped` (avoids wizard loop).
 */
export const ONBOARDING_SKIPPED_WIZARD_EXIT_LOCATION_STATE = {
  onboardingSkippedWizardExit: true as const,
};

export const ADMIN_INITIAL_STEP_META: MultiStepperStepMeta[] = [
  { id: 'project_setup', label: 'Project' },
  { id: 'integration', label: 'Integration' },
  { id: 'po_settings', label: 'Purchase Order Settings' },
  { id: 'bills', label: 'Bills' },
  { id: 'accounting_codes', label: 'Accounting Codes' },
  { id: 'resources', label: 'Resources' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'employees', label: 'Workers' },
  { id: 'tracking_category', label: 'Tracking Category' },
];

/** Stable step ids used across the wizard (analytics, persistence, mapping). */
export type OnboardingStepId =
  | 'project_setup'
  | 'integration'
  | 'po_settings'
  | 'bills'
  | 'accounting_codes'
  | 'resources'
  | 'suppliers'
  | 'employees'
  | 'tracking_category';

/**
 * These are the labels from the shared `events.yaml` contract and intentionally differ from the
 * stepper labels in {@link ADMIN_INITIAL_STEP_META} (e.g. "PO Settings" not
 * "Purchase Order Settings", "Workers/Employees" not "Workers").
 */
export const ONBOARDING_STEP_POSTHOG_LABEL: Record<OnboardingStepId, string> = {
  project_setup: 'Project Settings',
  integration: 'Integration',
  po_settings: 'PO Settings',
  bills: 'Bills',
  accounting_codes: 'Accounting Codes',
  resources: 'Resources',
  suppliers: 'Suppliers',
  employees: 'Workers/Employees',
  tracking_category: 'Tracking Category',
};

/** Narrows an arbitrary step id string to a known {@link OnboardingStepId}. */
export const isOnboardingStepId = (
  value: string | undefined,
): value is OnboardingStepId =>
  value !== undefined && value in ONBOARDING_STEP_POSTHOG_LABEL;

/** Step indices aligned with `ADMIN_INITIAL_STEP_META` (full 9-step flow including Xero tracking). */
export const ADMIN_INITIAL_STEP_INDEX = {
  PROJECT: 0,
  INTEGRATION: 1,
  PO_SETTINGS: 2,
  BILLS: 3,
  ACCOUNTING_CODES: 4,
  RESOURCES: 5,
  SUPPLIERS: 6,
  EMPLOYEES: 7,
  TRACKING_CATEGORY: 8,
} as const;

export const STEP_HEADINGS: Record<
  number,
  { kicker: string; title: string; description: string }
> = {
  0: {
    kicker: 'STEP 1 OF 8',
    title: 'Project Settings',
    description:
      'Configure how project numbers are generated in Varicon. This sets the default prefix and starting number for all new projects.',
  },
  1: {
    kicker: 'STEP 2 OF 8',
    title: 'Accounting Software',
    description:
      'Connect your accounting software so Varicon can automatically import your suppliers, expense codes, and tax settings. This is the foundation for everything else in the setup.',
  },
  2: {
    kicker: 'STEP 3 OF 8',
    title: 'Purchase Order Settings',
    description:
      'Configure how purchase order numbers are generated and set up the approver workflow for purchase orders.',
  },
  3: {
    kicker: 'STEP 4 OF 8',
    title: 'Bill Integration Setup',
    description:
      'Configure how bills sync between Varicon and your accounting system.',
  },
  4: {
    kicker: 'STEP 5 OF 8',
    title: 'Review Accounting Codes',
    description:
      'Many of your accounting codes from your accounting software are not applicable in Varicon. For best user experience we recommend archiving any accounting codes that are not required in Varicon.',
  },
  5: {
    kicker: 'STEP 6 OF 8',
    title: 'Map Resources To Accounting Code',
    description: 'Assign each resource to its corresponding accounting code.',
  },
  6: {
    kicker: 'STEP 7 OF 9',
    title: 'Review Suppliers',
    description:
      'Review suppliers imported from your accounting software and assign resource types so Varicon can categorise them correctly.',
  },
  7: {
    kicker: 'STEP 8 OF 9',
    title: 'Review Workers',
    description:
      "We've pulled your workers from your accounting software. Review their details below, assign an employment type and role for each, then proceed. Workers will be created as Inactive — activate them after induction.",
  },
  8: {
    kicker: 'STEP 9 OF 9',
    title: 'Setup Tracking Categories',
    description:
      'Tracking Category in Xero allows you to group transactions by project, cost centers and other dimensions for reporting.',
  },
};

export const NEXT_STEPS = [
  'Create and manage projects, documents, and galleries in one place',
  'Create and manage daywork records, purchase orders, site dockets, and bills',
  'Organize forms, files, and supplier information centrally',
  'Manage assets and streamline resource allocation',
] as const;
